import { useEffect, useState } from "react";

import { useCreateRisk, useCriticalRisks, useEditRisk, useFetchRisk, useRiskCategories, useRiskMatrix, useRiskSummary } from "../mutation";
import { AutocompleteOption } from "../../../shared/components/ControlledAutocomplete";
import { usePhase } from "../../projectOverview/hooks/usePhase";
import { useSearchProjects } from "../../projects/mutations";

export const useRisk = ({ editStatus, riskId, projectId, refetch }: { editStatus: boolean, riskId: string | null, projectId: string | undefined, refetch: () => void }) => {

    const { riskData, isRiskPending, refetch: refetchRisk } = useFetchRisk({ riskId: riskId });

    const phaseData = usePhase();
    const searchProjects = useSearchProjects();

    const createRisk = useCreateRisk();
    const editRisk = useEditRisk();

    const { removeCache: removeCriticalRisksCache } = useCriticalRisks(projectId);
    const { removeCache: removeRiskCategoriesCache } = useRiskCategories(projectId);
    const { removeCache: removeRiskMatrixCache } = useRiskMatrix(projectId);
    const { removeCache: removeRiskSummaryCache } = useRiskSummary(projectId);

    const [overviewData, setOverviewData] = useState<{
        riskTitle: null | string
        riskDescription: null | string
        riskCategory: null | string
        riskStatus: null | string
        riskReviewDate: null | string
        type: 'Project' | 'Portfolio' | 'Programme',
        phase: null | string,
        assigned: null | string,
        project: null | AutocompleteOption,
        portfolio: null | AutocompleteOption,
        programme: null | AutocompleteOption,
    }>({
        riskTitle: null,
        riskDescription: null,
        riskCategory: null,
        riskStatus: null,
        riskReviewDate: null,
        type: 'Project',
        phase: null,
        assigned: null,
        project: null,
        portfolio: null,
        programme: null,
    });
    
    const [overViewDataErrors, setOverViewDataErrors] = useState<{
            riskTitle: string | null,
            riskDescription: string | null,
            riskCategory: string | null,
            riskStatus: string | null,
            riskReviewDate: string | null,
            type: string | null,
            phase: string | null,
            assigned: null | string,
            project: string | null,
            portfolio: string | null,
            programme: string | null,
        }>({
            riskTitle: null,
            riskDescription: null,
            riskCategory: null,
            riskStatus: null,
            riskReviewDate: null,
            type: null,
            phase: null,
            assigned: null,
            project: null,
            portfolio: null,
            programme: null,
        });
    
        const validateFields = () => {
            const errors: any = {};
            if (!overviewData.riskTitle) {
                errors.riskTitle = "Please enter a title";
            }
            if (!overviewData.riskDescription) {
                errors.riskDescription = "Please enter a description";
            }
            if (!overviewData.riskCategory) {
                errors.riskCategory = "Please select a category";
            }
            if (!overviewData.riskStatus) {
                errors.riskStatus = "Please select a status";
            }
            if (!overviewData.riskReviewDate) {
                errors.riskReviewDate = "Please select a review date";
            }
            if (!overviewData.type) {
                errors.type = "Please select a type";
            }
            if (!overviewData.phase) {
                errors.phase = "Please select a phase";
            }
            if(!overviewData.assigned) {
                errors.assigned = "Please select a assigned to";
            }
            setOverViewDataErrors(errors);
            return Object.keys(errors).length === 0;
        };

        const handleOnChange = (key: string, value: any) => {
            setOverviewData((prev: any) => ({ ...prev, [key]: value }));
            if (overViewDataErrors && overViewDataErrors[key as keyof typeof overViewDataErrors]) {
                setOverViewDataErrors?.((prev: any) => ({ ...prev, [key]: null }));
            }
        };


    const onSaveRisk = async () => {
        const payload = {
            title: overviewData.riskTitle,
            risk_description: overviewData.riskDescription,
            category: overviewData.riskCategory,
            status: overviewData.riskStatus,
            user_id: overviewData.assigned,
            review_date: overviewData.riskReviewDate ? overviewData.riskReviewDate : null,
            project_id: overviewData.project?.id,
            project_tree_id: overviewData.phase,
            programme_id: overviewData.programme?.id,
            portfolio_id: overviewData.portfolio?.id,
            type: overviewData.type,
        };
        if (!validateFields()) return;
        if (editStatus && riskId) {
            await editRisk.mutateAsync({ riskId: riskId.toString(), data: payload });
        } else {
            await createRisk.mutateAsync({ data: payload });
        }
        refetch();
        refetchRisk();
        removeCriticalRisksCache();
        removeRiskCategoriesCache();
        removeRiskMatrixCache();
        removeRiskSummaryCache();
    };

    const fetchProjectDetails = async () => {
        const params: Record<string, any> = {
            project_id: projectId
        };
        const response = await searchProjects.mutateAsync({ params });
        setOverviewData({
            ...overviewData,
            project: {
                id: response.data[0].id,
                label: response.data[0].title
            }
        });
    };

    useEffect(() => {
        if (projectId && !riskId)
            fetchProjectDetails();
    }, [projectId]);

    useEffect(() => {
        if (riskId) {
            setOverviewData({
                riskTitle: riskData?.risk_title,
                riskDescription: riskData?.risk_description,
                riskCategory: riskData?.risk_category,
                riskStatus: riskData?.risk_status,
                riskReviewDate: riskData?.review_date,
                type: riskData?.type,
                phase: riskData?.project_tree_id,
                assigned: riskData?.user_id,
                project: { id: riskData?.project_id, label: riskData?.project_title },
                portfolio: { id: riskData?.portfolio_id, label: riskData?.portfolio_name },
                programme: { id: riskData?.programme_id, label: riskData?.programme_name }
            });
        }
    }, [riskId, riskData, isRiskPending]);

    useEffect(() => {
        if (overviewData.project?.id && !Array.isArray(overviewData.project))
            phaseData.mutateAsync(overviewData.project.id as string);
    }, [overviewData.project]);

    return {
        onSaveRisk,
        overviewData, setOverviewData,
        overViewDataErrors, handleOnChange,
        isRiskPending,
        isSaving: createRisk.isPending || editRisk.isPending,
        phaseOptions: phaseData.data?.data || [],
        isPhasePending: phaseData.isPending,
    };

};